import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import { InformationCircle, Minus, Plus } from '../../assets/svgs/icons';
import { ChevronRight } from '../../assets/svgs/icons';
import { notifyErrorCustom } from '../../utils/notify';

export const AttachReferenceArt = ({
  onImageSelect,
  acceptedFormats = 'image/*',
  maxFileSize = 10 * 1024 * 1024, // 10MB por padrão
  label,
  className = '',
  disabled = false,
  options = null,
  showPreview = false,
  verticalLayout = false,
  setOpen = null,
  open = false,
  onSubmenuOpen = null,
  hideExpandButton = false,
  menuPosition = 'bottom',
  menuClassName = '',
}) => {
  const { t } = useTranslation();
  const displayLabel = label || t('attach_reference_art.attach_label');
  const fileInputRef = useRef(null);
  const fileInputRefs = useRef({});
  const containerRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [error, setError] = useState(null);
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState(null);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const submenuRefs = useRef({});
  const [submenuPosition, setSubmenuPosition] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setHoveredOptionIndex(null);
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setHoveredOptionIndex(null);
    }
  }, [open]);

  // Função para calcular a posição dinâmica do submenu
  const calculateSubmenuPosition = (optionIndex, buttonElement) => {
    if (!buttonElement) return {};

    const rect = buttonElement.getBoundingClientRect();
    const submenuWidth = 280; // min-width do submenu
    const padding = 8;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Tentar obter a altura real do submenu se já estiver renderizado
    const submenuElement = submenuRefs.current[optionIndex]?.submenu;
    let submenuHeight = 150; // altura estimada padrão (mais realista)

    if (submenuElement) {
      const submenuRect = submenuElement.getBoundingClientRect();
      submenuHeight = submenuRect.height || 150;
    }

    const position = {
      top: undefined,
      bottom: undefined,
      left: undefined,
      right: undefined,
    };

    // Verificar espaço à direita
    const spaceRight = viewportWidth - rect.right;
    const spaceLeft = rect.left;

    // Verificar espaço abaixo (considerando a altura real do submenu)
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Posicionamento horizontal
    if (spaceRight >= submenuWidth + padding) {
      // Abrir à direita (padrão)
      position.left = '100%';
      position.right = 'auto';
    } else if (spaceLeft >= submenuWidth + padding) {
      // Abrir à esquerda
      position.right = '100%';
      position.left = 'auto';
    } else {
      // Se não houver espaço suficiente em nenhum lado, usar a opção com mais espaço
      if (spaceRight > spaceLeft) {
        position.left = '100%';
        position.right = 'auto';
      } else {
        position.right = '100%';
        position.left = 'auto';
      }
    }

    // Posicionamento vertical - verificar se há espaço suficiente abaixo
    // Considerar margem de segurança maior
    const minSpaceRequired = submenuHeight + padding + 50; // 50px de margem extra para o botão "Gerar arte"

    if (spaceBelow >= minSpaceRequired) {
      // Abrir abaixo (padrão)
      position.top = '0';
      position.bottom = 'auto';
    } else if (spaceAbove >= minSpaceRequired) {
      // Abrir acima
      position.bottom = '0';
      position.top = 'auto';
    } else {
      // Se não houver espaço suficiente, usar a opção com mais espaço
      if (spaceBelow > spaceAbove) {
        position.top = '0';
        position.bottom = 'auto';
      } else {
        position.bottom = '0';
        position.top = 'auto';
      }
    }

    return position;
  };

  // Atualizar posição quando o submenu for aberto ou a janela for redimensionada
  useEffect(() => {
    const updatePositions = () => {
      if (hoveredOptionIndex !== null) {
        const buttonElement = submenuRefs.current[hoveredOptionIndex]?.button;
        if (buttonElement) {
          // Usar setTimeout para garantir que o submenu já foi renderizado
          setTimeout(() => {
            const position = calculateSubmenuPosition(hoveredOptionIndex, buttonElement);
            setSubmenuPosition(prev => ({
              ...prev,
              [hoveredOptionIndex]: position
            }));
          }, 0);
        }
      }
    };

    updatePositions();
    window.addEventListener('resize', updatePositions);
    window.addEventListener('scroll', updatePositions, true);

    return () => {
      window.removeEventListener('resize', updatePositions);
      window.removeEventListener('scroll', updatePositions, true);
    };
  }, [hoveredOptionIndex]);

  useEffect(() => {
    if (hoveredOptionIndex !== null && onSubmenuOpen) {
      const option = options?.[hoveredOptionIndex];
      // Verificar se é o botão "Imagens" (id: 'type') que tem submenu
      if (option && option.id === 'type' && option.submenu) {
        onSubmenuOpen();
      }
    }
  }, [hoveredOptionIndex, onSubmenuOpen, options]);

  const handleCircleClick = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  const handleButtonClick = (optionIndex = null) => {
    if (disabled) return;

    if (options && optionIndex !== null) {

      const option = options[optionIndex];
      if (option.disabled) return;

      // Múltiplas opções: usar o ref específico da opção
      const inputRef = fileInputRefs.current[optionIndex];
      if (inputRef) {
        inputRef.click();
      }
    } else {
      // Opção única: usar o ref padrão
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const handleFileChange = (event, optionIndex = null, customOnSelect = null) => {
    const file = event.target.files[0];

    if (!file) return;

    setError(null);

    // Validação de tamanho
    if (file.size > maxFileSize) {
      const errorMessage = t('attach_reference_art.file_too_large');
      setError(errorMessage);
      notifyErrorCustom(
        errorMessage,
        t('chatsocket.errors.file_too_large_title', { defaultValue: 'Arquivo muito grande' }),
        8000,
        { width: '380px', right: 60 }
      );
      return;
    }

    // Validação de tipo
    if (!file.type.match(/^image\//)) {
      setError(t('attach_reference_art.invalid_format'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = {
        file,
        base64: e.target.result,
        name: file.name,
        size: file.size,
        type: file.type,
      };

      // Sempre atualiza o estado interno para o preview funcionar
      setSelectedImage(imageData);

      // Se houver opção customizada, usar seu callback
      if (customOnSelect) {
        customOnSelect(imageData);
      } else {
        // Comportamento padrão
        if (onImageSelect) {
          onImageSelect(imageData);
        }
      }

      setOpen(false); // Fecha após selecionar
    };

    reader.onerror = () => {
      setError(t('attach_reference_art.read_error'));
    };

    reader.readAsDataURL(file);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setSelectedImage(null);
    setError(null);
    setOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onImageSelect) {
      onImageSelect(null);
    }
  };

  return (
    <div id="attach-reference-art" className={`${styles.container} ${className}`} ref={containerRef}>
      {/* Preview da imagem - aparece acima do botão quando há imagem selecionada */}
      {showPreview && selectedImage && (
        <div className={styles.image_preview_container_flex}>
          <img
            src={selectedImage.base64}
            alt={t('attach_reference_art.preview')}
            className={styles.preview_image}
          />
          <button
            className={styles.remove_image_button}
            onClick={handleRemove}
            type="button"
            title={t('attach_reference_art.remove_image')}
            aria-label={t('attach_reference_art.remove_image')}
          >
            ×
          </button>
        </div>
      )}

      {!hideExpandButton && (
        <div className={styles.horizontal_wrapper}>
          {/* Botão circular com + / - */}
          <button
            id="image-selector-button"
            className={styles.expand_button}
            onClick={handleCircleClick}
            disabled={disabled}
            aria-label={open ? t('attach_reference_art.close_menu') : t('attach_reference_art.open_menu')}
          >
            {open ? <Minus color='#fff' /> : <Plus color='#fff' />}
          </button>
        </div>
      )}

      {/* Menu expandido */}
      {open && (
        <div className={`${styles.expand_content} ${verticalLayout ? styles.vertical_layout : ''} ${menuPosition === 'top' ? styles.menu_top : ''} ${menuClassName}`}>
          {options && options.length > 0 ? (
            // Múltiplas opções
            options.map((option, index) => {
              // const isEditButton = option.label?.toLowerCase().includes('alterar') || 
              //                       option.label?.toLowerCase().includes('editar') ||
              //                       option.iconType === 'edit';
              // const isAttachButton = option.label?.toLowerCase().includes('anexar') ||
              //                         option.iconType === 'attach';

              const isOptionDisabled = option.disabled || disabled;
              const hasSubmenu = option.submenu && option.submenu.length > 0;

              return (
                <div
                  key={index}
                  className={styles.option_wrapper} // ADICIONAR ESTA CLASSE
                  onMouseEnter={() => {
                    if (hasSubmenu && !isOptionDisabled) {
                      if (hoveredOptionIndex === index) {
                        setHoveredOptionIndex(null);
                      } else {
                        setHoveredOptionIndex(index);
                      }
                    }
                  }}
                  onMouseLeave={() => { }} // ADICIONAR
                >
                  <button
                    id={option.id ? `image-selector-${option.id}` : undefined}
                    ref={(el) => {
                      if (el && hasSubmenu) {
                        if (!submenuRefs.current[index]) {
                          submenuRefs.current[index] = {};
                        }
                        submenuRefs.current[index].button = el;
                      }
                    }}
                    className={`${styles.action_btn} ${hasSubmenu && hoveredOptionIndex === index ? styles.submenu_open : ''}`}
                    onClick={() => {
                      if (hasSubmenu) return;
                      if (option.openModalOnly && option.onSelect) {
                        option.onSelect();
                        setOpen(false);
                      } else {
                        handleButtonClick(index);
                      }
                    }}
                    type="button"
                    disabled={isOptionDisabled}
                    style={{
                      opacity: isOptionDisabled ? 0.5 : 1,
                      cursor: isOptionDisabled ? 'not-allowed' : 'pointer',
                      color: isOptionDisabled ? '#999' : 'inherit',
                      backgroundColor: isOptionDisabled ? '#f5f5f5' : 'transparent'
                    }}
                  >
                    {/* Ícone de informação para o card "Anexar arte de referência" */}
                    {/* {option.id === 'reference' && (
                      <div className={styles.info_icon_wrapper}>
                        <div 
                          className={styles.info_icon_container}
                          onMouseEnter={() => setShowInfoTooltip(true)}
                          onMouseLeave={() => setShowInfoTooltip(false)}
                        >
                          <InformationCircle color="#6A0098" />
                          {showInfoTooltip && (
                            <div className={styles.info_tooltip}>
                              {t('attach_reference_art_color_info') || 'Se o usuário quiser manter as cores da arte que está subindo como referência, basta solicitar no prompt. Caso contrário, nossa IA substituirá as cores para a paleta cadastrada na conta do usuário.'}
                            </div>
                          )}
                        </div>
                      </div>
                    )} */}
                    <div className={styles.button_content}>
                      <span className={styles.button_text}>{option.label}</span>
                      {option.description && (
                        <span className={styles.button_description}>{option.description}</span>
                      )}
                    </div>
                    {hasSubmenu && (
                      <ChevronRight
                        color={isOptionDisabled ? '#999' : '#190027'}
                        size="12"
                        className={styles.submenu_arrow}
                      />
                    )}
                  </button>
                  {hasSubmenu && hoveredOptionIndex === index && (
                    <div
                      ref={(el) => {
                        if (el) {
                          if (!submenuRefs.current[index]) {
                            submenuRefs.current[index] = {};
                          }
                          submenuRefs.current[index].submenu = el;
                        }
                      }}
                      className={styles.submenu}
                      style={submenuPosition[index] || {}}
                      onMouseEnter={() => setHoveredOptionIndex(index)}
                      onMouseLeave={() => {
                        setTimeout(() => {
                          setHoveredOptionIndex(null);
                        }, 100);
                      }}
                    >
                      {option.submenu.map((subOption, subIndex) => (
                        <button
                          id={subIndex === 0 ? 'image-selector' : undefined}
                          key={subIndex}
                          className={styles.submenu_btn}
                          onClick={() => {
                            if (subOption.onSelect) {
                              subOption.onSelect();
                              setOpen(false);
                            } else {
                              const subInputRef = fileInputRefs.current[`${index}-${subIndex}`];
                              if (subInputRef) {
                                subInputRef.click();
                              }
                            }
                          }}
                          type="button"
                        >
                          <div className={styles.button_content}>
                            <span className={styles.button_text}>{subOption.label}</span>
                            {subOption.description && (
                              <span className={styles.button_description}>{subOption.description}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            // Opção única (comportamento padrão)
            <button
              className={styles.action_btn}
              onClick={handleButtonClick}
              type="button"
              disabled={disabled}
              title={displayLabel}
            >
              {/* <Clips color="currentColor" width={16} height={16} /> */}
              {displayLabel}
            </button>
          )}
        </div>
      )}

      {/* Inputs de arquivo */}
      {options && options.length > 0 ? (
        // Múltiplos inputs para múltiplas opções
        options.map((option, index) => {
          const inputs = [
            <input
              key={index}
              ref={(el) => {
                if (el) {
                  fileInputRefs.current[index] = el;
                }
              }}
              type="file"
              accept={acceptedFormats}
              onChange={(e) => handleFileChange(e, index, option.onSelect)}
              style={{ display: 'none' }}
              disabled={disabled || option.disabled}
            />
          ];
          if (option.submenu && option.submenu.length > 0) {
            option.submenu.forEach((subOption, subIndex) => {
              inputs.push(
                <input
                  key={`${index}-${subIndex}`}
                  ref={(el) => {
                    if (el) {
                      fileInputRefs.current[`${index}-${subIndex}`] = el;
                    }
                  }}
                  type="file"
                  accept={acceptedFormats}
                  onChange={(e) => handleFileChange(e, `${index}-${subIndex}`, subOption.onSelect)}
                  style={{ display: 'none' }}
                  disabled={disabled || option.disabled}
                />
              );
            });
          }
          return inputs;
        }).flat()
      ) : (
        // Input único (comportamento padrão)
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />
      )}

      {/* Mensagem de erro */}
      {/* {error && (
        <div className={styles.error_message}>
          {error}
        </div>
      )} */}
    </div>
  );
};